import { Box, Button, Checkbox, MenuItem } from '@mui/material';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { FormCard } from 'components/Card';
import { AppAutocompleteField, AppSelectField } from 'components/form-fields';
import { InfoCard } from 'components/notification-cards';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import { HardSkillEntry } from 'utils/cv-form-db';
import { ButtonContainer } from '../../components/styledComponents';
import { initialValues, skillLevelOptions } from './constants';
import { useHardSkillsForm, useHardSkillsNames } from './hooks';
import { validateHardSkill } from './utils';

interface HardSkillsFormProps {
  mode?: 'add' | 'edit';
  initialValues?: HardSkillEntry;
  onSubmit: (hardSkill: HardSkillEntry) => void;
  onCancel: () => void;
}

const SubmitHardSkillButton = ({
  isEditMode,
  isComplete
}: {
  isEditMode: boolean;
  isComplete: boolean;
}) => (
  <ButtonContainer sx={{ pb: 1 }}>
    <Button
      fullWidth
      type="submit"
      data-testid={
        isEditMode ? 'save-hard-skill-button' : 'add-hard-skill-button'
      }
      color="secondary"
      variant="contained"
      sx={{ minWidth: 252 }}
      disabled={!isComplete}
    >
      {isEditMode
        ? getTrans('general.update')
        : getTrans('cvBuilderPage.hardSkills.button.addHardSkill.label')}
    </Button>
  </ButtonContainer>
);

const CancelHardSkillButton = ({ onCancel }: { onCancel: () => void }) => (
  <ButtonContainer sx={{ mt: 2 }}>
    <Button
      fullWidth
      data-testid="cancel-hard-skill-button"
      color="secondary"
      variant="outlined"
      sx={{ minWidth: 252 }}
      onClick={onCancel}
    >
      {getTrans('general.cancel')}
    </Button>
  </ButtonContainer>
);

const InnerHardSkillForm = ({
  mode = 'add',
  onCancel
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
}) => {
  const { values } = useFormikContext<HardSkillEntry>();

  const isEditMode = mode === 'edit';

  const { isComplete } = useHardSkillsForm(values);

  const { data: hardSkills } = useHardSkillsNames();

  return (
    <Form
      data-testid={`${isEditMode ? 'edit' : 'add'}-hard-skill-form`}
      style={{
        flexBasis: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <FormCard>
        <AppAutocompleteField
          name="name"
          label={getTrans('cvBuilderPage.hardSkills.form.hardSkill.label')}
          options={hardSkills ?? []}
          freeSolo={true}
        />

        <AppSelectField
          fullWidth
          displayValidationError
          data-dd-privacy="mask"
          variant="outlined"
          name="level"
          label={getTrans('cvBuilderPage.hardSkills.form.level.label')}
        >
          {skillLevelOptions.map(option => (
            <MenuItem
              key={`${option.key}}`}
              data-testid={`level-select-field-option-${option.key}`}
              value={option.key}
            >
              {getTrans(option.value)}
            </MenuItem>
          ))}
        </AppSelectField>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 2 }}>
          <Field as={Checkbox} type="checkbox" name="professionallyUsed" />
          <Text>
            {getTrans('cvBuilderPage.hardSkills.form.professionallyUsed.label')}
          </Text>
        </Box>
        <InfoCard sx={{ mb: 2 }}>
          <Text variant="body2">
            <b>{getTrans('cvBuilderPage.hardSkills.form.infoCard.body1')}</b>
          </Text>
          <Text variant="body2" mt={1}>
            <b>
              {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part1')}
            </b>
            {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part2')}
            <br />
            <b>
              {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part3')}
            </b>
            {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part4')}
            <br />
            <b>
              {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part5')}
            </b>
            {getTrans('cvBuilderPage.hardSkills.form.infoCard.body2.part6')}
          </Text>
        </InfoCard>
      </FormCard>
      <Box sx={{ flex: 1 }} />
      <SubmitHardSkillButton isEditMode={isEditMode} isComplete={isComplete} />
      <CancelHardSkillButton onCancel={onCancel} />
    </Form>
  );
};

export const HardSkillsForm = ({
  mode = 'add',
  initialValues: editedValues,
  onSubmit,
  onCancel
}: HardSkillsFormProps) => (
  <Formik
    initialValues={editedValues || initialValues}
    onSubmit={onSubmit}
    validate={validateHardSkill}
    enableReinitialize
  >
    <InnerHardSkillForm mode={mode} onCancel={onCancel} />
  </Formik>
);
